import { forwardRef } from 'react';
import { Controller, type FieldValues, type Control } from 'react-hook-form';

import { Combobox } from './combobox';
import type { ComboboxFieldProps } from './combobox.types';

/**
 * React Hook Form wrapper for Combobox
 *
 * Integrates Combobox with React Hook Form for:
 * - Automatic form validation
 * - Error display
 * - Field registration
 * - Controlled form state
 *
 * @example
 * ```tsx
 * import { useForm } from 'react-hook-form';
 * import { ComboboxField } from '../../index';
 *
 * function MyForm() {
 *   const { control, handleSubmit } = useForm({
 *     defaultValues: { country: '' }
 *   });
 *
 *   return (
 *     <form onSubmit={handleSubmit(onSubmit)}>
 *       <ComboboxField
 *         control={control}
 *         name="country"
 *         label="Country"
 *         options={countries}
 *         rules={{ required: 'Country is required' }}
 *         search
 *       />
 *     </form>
 *   );
 * }
 * ```
 */
function ComboboxFieldInner<
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string | number = string,
  TMulti extends boolean = false,
>(
  {
    // React Hook Form props
    control,
    name,
    rules,
    shouldUnregister,
    defaultValue,

    // Label & Description
    label,
    description,
    helperText,
    showError = true,

    // Layout
    layout = 'vertical',
    labelWidth,

    // Required indicator
    required,
    requiredIndicator = '*',

    // Combobox props
    ...comboboxProps
  }: ComboboxFieldProps<TFieldValues, TValue, TMulti>,
  ref: React.Ref<HTMLDivElement>
) {
  // Cast control for Controller compatibility - TFieldValues inference happens at call site
  return (
    <Controller
      control={control as Control<FieldValues>}
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const { error } = fieldState;
        const hasError = !!error;

        // Determine required state from rules or prop
        const isRequired = required ?? !!rules?.required;

        // Build ARIA attributes
        const errorId = hasError ? `${name}-error` : undefined;
        const descriptionId = description ? `${name}-description` : undefined;
        const helperTextId = helperText ? `${name}-helper` : undefined;

        const ariaDescribedBy =
          [descriptionId, helperTextId, errorId].filter(Boolean).join(' ') || undefined;

        // Layout classes
        const containerClasses =
          layout === 'horizontal' ? 'flex items-start gap-4' : 'flex flex-col gap-1';

        const labelClasses =
          layout === 'horizontal'
            ? `flex-shrink-0 pt-2 ${labelWidth ? `w-${labelWidth}` : 'w-32'}`
            : '';

        const fieldClasses = layout === 'horizontal' ? 'flex-1' : '';

        return (
          <div className={`combobox-field ${containerClasses}`}>
            {/* Label */}
            {label && (
              <label
                htmlFor={name}
                className={`combobox-field-label block text-sm font-medium text-gray-700 ${labelClasses}`}
              >
                {label}
                {isRequired && requiredIndicator && (
                  <span className="combobox-field-required text-red-500 ml-1">
                    {requiredIndicator}
                  </span>
                )}
              </label>
            )}

            <div className={`combobox-field-wrapper ${fieldClasses}`}>
              {/* Description */}
              {description && (
                <p
                  id={descriptionId}
                  className="combobox-field-description text-sm text-gray-600 mb-1"
                >
                  {description}
                </p>
              )}

              {/* Combobox */}
              <Combobox
                ref={ref}
                {...comboboxProps}
                id={name}
                name={name}
                value={field.value}
                onChange={field.onChange}
                error={error?.message}
                required={isRequired}
                aria-describedby={ariaDescribedBy}
                aria-invalid={hasError}
              />

              {/* Helper Text */}
              {helperText && !hasError && (
                <p id={helperTextId} className="combobox-field-helper text-sm text-gray-500 mt-1">
                  {helperText}
                </p>
              )}

              {/* Error Message */}
              {showError && hasError && error?.message && (
                <p
                  id={errorId}
                  className="combobox-field-error text-sm text-red-600 mt-1"
                  role="alert"
                >
                  {error.message}
                </p>
              )}
            </div>
          </div>
        );
      }}
    />
  );
}

export const ComboboxField = forwardRef(ComboboxFieldInner) as <
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string | number = string,
  TMulti extends boolean = false,
>(
  props: ComboboxFieldProps<TFieldValues, TValue, TMulti> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

// Add displayName directly to the function
(ComboboxField as React.ForwardRefExoticComponent<unknown>).displayName = 'ComboboxField';
